import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import type { User, AuthResponse } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, recaptchaToken?: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    fullName: string,
    companyName?: string,
    recaptchaToken?: string,
    extra?: { firstName?: string; lastName?: string; trialToken?: string },
  ) => Promise<void>;
  googleLogin: (tokenResponse: any) => Promise<void>;
  shopifyLogin: (tokenResponse: AuthResponse) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string, recaptchaToken?: string) => {
    const response = await authAPI.login(email, password, recaptchaToken);
    handleAuthResponse(response.data);
  };

  const register = async (
    email: string,
    password: string,
    fullName: string,
    companyName?: string,
    recaptchaToken?: string,
    extra?: { firstName?: string; lastName?: string; trialToken?: string },
  ) => {
    const response = await authAPI.register({
      email,
      password,
      full_name: fullName,
      company_name: companyName,
      recaptcha_token: recaptchaToken,
      // Plan-TrialSignupLink.md §H/§K — First/Last carried alongside the
      // backend-compatible `full_name`; `trial_token` (from ?trial=) grants the
      // 7-day trial + Live Avatar when it resolves server-side.
      first_name: extra?.firstName,
      last_name: extra?.lastName,
      trial_token: extra?.trialToken,
    });
    handleAuthResponse(response.data);
  };

  const googleLogin = async (tokenResponse: any) => {
    // Decode Google token to get user info
    const response = await authAPI.googleAuth({
      google_token: tokenResponse.credential,
      full_name: tokenResponse.name || 'User',
      email: tokenResponse.email,
      google_id: tokenResponse.sub,
      profile_picture: tokenResponse.picture,
      // Optional on the backend model: Google omits given_name/family_name when
      // the `profile` scope is absent, so these degrade to undefined and are
      // dropped from the JSON body rather than failing sign-in.
      first_name: tokenResponse.given_name,
      last_name: tokenResponse.family_name,
      // Plan-TrialSignupLink.md §H — carries the `?trial=<token>` param through
      // the Google signup path so it grants the 7-day trial, exactly as
      // `register` already does. Undefined outside a trial link, in which case
      // it is dropped from the JSON body.
      trial_token: tokenResponse.trialToken,
    });
    handleAuthResponse(response.data);
  };

  // Shopify OAuth callback already exchanged the code for a Token on the
  // backend (POST /api/auth/shopify). This just persists the result, mirroring
  // googleLogin's final step. See Plan-ShopifySignInLogin.md.
  const shopifyLogin = (tokenResponse: AuthResponse) => {
    handleAuthResponse(tokenResponse);
  };

  const handleAuthResponse = (data: AuthResponse) => {
    localStorage.setItem('token', data.access_token);
    setUser(data.user);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    authAPI.logout().catch(() => { });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        googleLogin,
        shopifyLogin,
        logout,
        setUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
